import { useEffect, useState, lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import Layout from './components/layout/Layout'
import HomePage from './pages/HomePage'
import { AuthProvider } from './contexts/AuthContext'
import ErrorBoundary from './components/ErrorBoundary'
import LoadingScreen from './components/ui/LoadingScreen'
import { setupGlobalCleanup } from './lib/performanceUtils'

// Rota bazlı kod bölme — ilk yüklemede yalnızca ana sayfa paketi iner
const ProductsPage = lazy(() => import('./pages/ProductsPage'))
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'))
const CartPage = lazy(() => import('./pages/CartPage'))
const OrderPage = lazy(() => import('./pages/OrderPage'))
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'))
const OrderTrackingPageSimple = lazy(() => import('./pages/OrderTrackingPageSimple'))
const OrderDetailsPage = lazy(() => import('./pages/OrderDetailsPage'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'))
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const VisionMissionPage = lazy(() => import('./pages/VisionMissionPage'))
const QualityPolicyPage = lazy(() => import('./pages/QualityPolicyPage'))
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'))
const ConfirmationPage = lazy(() => import('./pages/auth/ConfirmationPage'))
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const OrdersPage = lazy(() => import('./pages/OrdersPage'))
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'))

// Admin paneli — genel ziyaretçiler için ayrı paketlerde tutulur
const AdminLoginPage = lazy(() => import('./pages/admin/LoginPage'))
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const AdminOrdersPage = lazy(() => import('./pages/admin/OrdersPage'))
const OrderDetailPage = lazy(() => import('./pages/admin/OrderDetailPage'))
const AdminProductsPage = lazy(() => import('./pages/admin/ProductsPage'))
const AdminCustomersPage = lazy(() => import('./pages/admin/CustomersPage'))
const CouponsPage = lazy(() => import('./pages/admin/CouponsPage'))
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage'))
const AdminProfilePage = lazy(() => import('./pages/admin/ProfilePage'))
const AdminSettingsPage = lazy(() => import('./pages/admin/SettingsPage'))
const CouriersPage = lazy(() => import('./pages/admin/CouriersPage'))
const EmailLogsPage = lazy(() => import('./pages/admin/EmailLogsPage'))
const DistrictsPage = lazy(() => import('./pages/admin/DistrictsPage'))

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
    </div>
  )
}

function App() {
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  // Global cleanup setup
  useEffect(() => {
    setupGlobalCleanup()

    // Kısa açılış ekranı
    const timer = setTimeout(() => {
      setIsInitialLoading(false)
    }, 600)

    return () => clearTimeout(timer)
  }, [])

  return (
    <ErrorBoundary>
      <LoadingScreen isLoading={isInitialLoading} />
      <AuthProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Auth Routes */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/register" element={<RegisterPage />} />
            <Route path="/auth/confirm" element={<ConfirmationPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

            {/* Public Routes */}
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="urunler" element={<ProductsPage />} />
              <Route path="urun/:id" element={<ProductDetailPage />} />
              <Route path="sepet" element={<CartPage />} />
              <Route path="siparis" element={<OrderPage />} />
              <Route path="siparis-basarili" element={<OrderSuccessPage />} />
              <Route path="siparis-takip" element={<OrderTrackingPageSimple />} />
              <Route path="siparis-takip/:orderId" element={<OrderDetailsPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="favorites" element={<FavoritesPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="hakkimizda" element={<AboutPage />} />
              <Route path="vizyon-misyon" element={<VisionMissionPage />} />
              <Route path="kalite-politikasi" element={<QualityPolicyPage />} />
              <Route path="gizlilik" element={<PrivacyPolicyPage />} />
              <Route path="kullanim-kosullari" element={<TermsOfServicePage />} />
            </Route>

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="siparisler" element={<AdminOrdersPage />} />
              <Route path="siparis/:orderId" element={<OrderDetailPage />} />
              <Route path="urunler" element={<AdminProductsPage />} />
              <Route path="musteriler" element={<AdminCustomersPage />} />
              <Route path="kuponlar" element={<CouponsPage />} />
              <Route path="raporlar" element={<ReportsPage />} />
              <Route path="profil" element={<AdminProfilePage />} />
              <Route path="ayarlar" element={<AdminSettingsPage />} />
              <Route path="kuryeler" element={<CouriersPage />} />
              <Route path="email-logs" element={<EmailLogsPage />} />
              <Route path="ilceler" element={<DistrictsPage />} />
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
